import React, { useEffect, useState } from "react";
import { PauseCircle, PlayCircle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Talent-only toggle: when ON, employers cannot start new conversations
 * with this talent (existing threads still work). Backed by
 * POST /messaging/pause-incoming + users.messaging_paused.
 */
export const PauseIncomingToggle = ({ compact = false }) => {
    const { user, refresh } = useAuth() || {};
    const [paused, setPaused] = useState(Boolean(user?.messaging_paused));
    const [busy, setBusy] = useState(false);

    // Keep local state in sync when the auth user hydrates later.
    useEffect(() => { setPaused(Boolean(user?.messaging_paused)); }, [user?.messaging_paused]);

    const toggle = async () => {
        const next = !paused;
        setBusy(true);
        try {
            await api.post("/messaging/pause-incoming", { paused: next });
            setPaused(next);
            // Pull the fresh user doc so other mounted components see the flip.
            refresh?.();
            toast.success(next
                ? "Incoming messages paused. Employers won't be able to start new threads."
                : "Incoming messages resumed.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const Icon = paused ? PlayCircle : PauseCircle;
    return (
        <button
            type="button"
            onClick={toggle}
            disabled={busy}
            data-testid="msg-pause-toggle"
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                paused
                    ? "border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
                    : "border-border bg-card text-foreground hover:bg-muted/40"
            } ${compact ? "px-2.5 py-1" : ""}`}
        >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
            {paused ? "Resume incoming messages" : "Pause incoming messages"}
        </button>
    );
};

export default PauseIncomingToggle;
